import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';
import LinearProgress from '@mui/material/LinearProgress';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
    audienceOptions,
    behaviorOptions,
    communicationStyleOptions,
    escalationTriggerOptions,
    goalOptions,
    industryOptions,
    mainJobOptions,
    offTopicHandlingOptions,
    personalityOptions,
    promptEngineerPhasePrompts,
    promptEngineerPhaseTitles,
    prohibitedActionOptions,
    taskApproachOptions,
    taskOptions,
    uncertaintyHandlingOptions
} from './promptEngineerConstants';

const renderSelect = (label, value, options, onChange, required = true) => (
    <FormControl fullWidth required={required}>
        <InputLabel>{label}</InputLabel>
        <Select
            label={label}
            value={value}
            onChange={(event) => onChange(event.target.value)}
        >
            {options.map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
        </Select>
    </FormControl>
);

const URL_PREFIX = 'https://';
const CUSTOMER_FACING_ACTION_LABELS = {
    create_technical_support_tickets: 'Technical Support',
    schedule_meetings: 'Appointment Scheduling',
    lead_opportunity_management: 'Sales & Lead Management',
    customer_service: 'Customer Service',
    order_account_management: 'Order & Account Management',
    knowledge_status_lookup: 'Knowledge & Status Lookup',
    notifications_follow_up: 'Notifications & Follow-up',
    workflow_automation: 'Workflow Automation'
};

const DEFAULT_ACTION_EMPTY_STATE = {
    title: 'No compatible connected services yet',
    message: 'You can continue setup now. Connect compatible services later to enable Business Actions.',
    onboardingGuidance: 'When compatible services are connected, you can return and enable capabilities for your AI.'
};

const stripUrlProtocol = (value = '') => value.replace(/^https?:\/\//i, '');

const buildHttpsUrl = (value = '') => {
    const normalized = stripUrlProtocol(value).trim();
    return normalized ? `${URL_PREFIX}${normalized}` : '';
};

function MultiSelectField({ label, value, options, onChange }) {
    const [open, setOpen] = useState(false);

    const preventDefault = (event) => {
        event.preventDefault();
    };

    const handleValueChange = (event) => {
        const nextValue = event.target.value;
        onChange(typeof nextValue === 'string' ? nextValue.split(',') : nextValue);
    };

    return (
        <FormControl fullWidth required>
            <InputLabel>{label}</InputLabel>
            <Select
                multiple
                label={label}
                value={value}
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                onChange={handleValueChange}
                renderValue={(selected) => (
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'flex-start',
                            columnGap: 0.75,
                            rowGap: 0.25,
                            width: '100%',
                            maxHeight: 88,
                            overflowY: 'auto',
                            overflowX: 'hidden'
                        }}
                    >
                        {selected.map((item, index) => (
                            <Typography
                                key={`${item}-${index}`}
                                component="span"
                                variant="body2"
                                sx={{
                                    whiteSpace: 'normal',
                                    overflowWrap: 'anywhere',
                                    lineHeight: 1.35
                                }}
                            >
                                {item}{index < selected.length - 1 ? ',' : ''}
                            </Typography>
                        ))}
                    </Box>
                )}
                SelectDisplayProps={{
                    onClick: () => setOpen((prev) => !prev)
                }}
                sx={{
                    '& .MuiSelect-select': {
                        display: 'block',
                        whiteSpace: 'normal',
                        overflowWrap: 'anywhere',
                        minHeight: 'auto',
                        py: 1.25,
                        pr: 4
                    }
                }}
                MenuProps={{
                    disableScrollLock: true,
                    anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left'
                    },
                    transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left'
                    },
                    PaperProps: {
                        sx: {
                            maxHeight: 360,
                            maxWidth: 'min(92vw, 640px)',
                            overflowX: 'hidden'
                        }
                    }
                }}
            >
                <ListSubheader disableSticky sx={{ backgroundColor: 'background.paper' }}>
                    <Stack direction="row" spacing={1} justifyContent="flex-start" alignItems="center">
                        <Button
                            variant="outlined"
                            size="small"
                            onMouseDown={preventDefault}
                            onClick={() => onChange([...options])}
                        >
                            Select All
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            onMouseDown={preventDefault}
                            onClick={() => onChange([])}
                        >
                            Clear All
                        </Button>
                    </Stack>
                </ListSubheader>
                {options.map((option) => (
                    <MenuItem key={option} value={option}>
                        <Checkbox checked={value.includes(option)} />
                        <ListItemText primary={option} />
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}

export default function PromptEngineerInterview({
    currentPhase,
    phasePosition,
    phaseCount,
    phaseTitle,
    phasePrompt,
    config,
    instructionName,
    canContinue,
    onBack,
    onContinue,
    onInstructionNameChange,
    onUpdate,
    onGenerateBusinessDescription,
    isGeneratingBusinessDescription,
    businessDescriptionGenerationError,
    businessActionInterviewMetadata,
    businessActionMetadataError,
    onBusinessActionSelectionsChange
}) {
    const [sourceUrl, setSourceUrl] = useState(config?.business?.descriptionSourceUrl || '');
    const [urlError, setUrlError] = useState('');
    const [replaceConfirmOpen, setReplaceConfirmOpen] = useState(false);
    const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
    const [pendingGenerationMode, setPendingGenerationMode] = useState('context');
    const [hasEditedGeneratedDescription, setHasEditedGeneratedDescription] = useState(false);

    const hasExistingDescription = Boolean(config?.business?.description?.trim());
    const descriptionSource = config?.business?.descriptionSource || 'manual';
    const isGeneratedDescription = descriptionSource === 'ai_url' || descriptionSource === 'ai_context';

    useEffect(() => {
        setHasEditedGeneratedDescription(false);
    }, [descriptionSource, config?.business?.descriptionGeneratedAt]);

    const parseGenerationError = (message) => {
        if (!message) return '';
        if (/invalid url/i.test(message)) {
            return 'Please enter a valid URL that starts with http:// or https://.';
        }
        return message;
    };

    const executeGenerate = async (mode, overwriteExisting = false) => {
        const trimmedSourceUrl = buildHttpsUrl(sourceUrl);
        setUrlError('');

        if (mode === 'url' && !trimmedSourceUrl) {
            setUrlError('Please enter a URL before generating from a page.');
            return;
        }

        try {
            await onGenerateBusinessDescription?.({
                sourceUrl: mode === 'url' ? trimmedSourceUrl : '',
                mode,
                overwriteExisting
            });
        } catch (error) {
            const fallbackError = parseGenerationError(error?.message || businessDescriptionGenerationError);
            if (fallbackError) {
                setUrlError(fallbackError);
            }
        }
    };

    const requestGenerate = (mode) => {
        if (!hasExistingDescription) {
            executeGenerate(mode);
            return;
        }
        setPendingGenerationMode(mode);
        setReplaceConfirmOpen(true);
    };

    const handleBusinessDescriptionChange = (event) => {
        const nextValue = event.target.value;
        if (isGeneratedDescription && nextValue !== config.business.description) {
            setHasEditedGeneratedDescription(true);
        }
        onUpdate('business', 'description', nextValue);
    };

    const getBusinessDescriptionChipLabel = () => {
        if (!isGeneratedDescription) {
            return '';
        }
        if (hasEditedGeneratedDescription) {
            return descriptionSource === 'ai_url' ? 'Website Generated + Edited' : 'Context Generated + Edited';
        }
        return descriptionSource === 'ai_url' ? 'Generated from Website' : 'Generated from Context';
    };

    const resolvedPhaseTitle = phaseTitle || promptEngineerPhaseTitles[currentPhase] || '';
    const resolvedPhasePrompt = phasePrompt || promptEngineerPhasePrompts[currentPhase] || '';
    const displayPhaseCount = phaseCount || promptEngineerPhaseTitles.length;
    const displayPhaseNumber = (phasePosition ?? currentPhase) + 1;
    const progressValue = (displayPhaseNumber / displayPhaseCount) * 100;

    const businessActionSelections = Array.isArray(config?.businessActionSelections)
        ? config.businessActionSelections
        : [];

    const actionDefinitions = Array.isArray(businessActionInterviewMetadata?.businessActions)
        ? businessActionInterviewMetadata.businessActions
        : [];
    const hasEligibleConnectedServices = Boolean(businessActionInterviewMetadata?.hasEligibleConnectedServices);
    const actionPhaseEmptyState = {
        ...DEFAULT_ACTION_EMPTY_STATE,
        ...(businessActionInterviewMetadata?.phase?.emptyState || {})
    };
    const productionActionOrder = [
        'create_technical_support_tickets',
        'customer_service',
        'lead_opportunity_management',
        'schedule_meetings',
        'knowledge_status_lookup'
    ];
    const availableSoonActionOrder = [
        'order_account_management',
        'notifications_follow_up',
        'workflow_automation'
    ];
    const resolveCustomerFacingLabel = (action) => CUSTOMER_FACING_ACTION_LABELS[action?.businessActionKey]
        || action?.businessActionLabel
        || '';

    const actionByKey = new Map(actionDefinitions.map((action) => [action.businessActionKey, action]));
    const productionActions = productionActionOrder
        .map((key) => actionByKey.get(key))
        .filter(Boolean)
        .filter((action) => (action?.capabilityState || 'production') === 'production');
    const availableSoonActions = availableSoonActionOrder
        .map((key) => actionByKey.get(key))
        .filter(Boolean)
        .filter((action) => (action?.capabilityState || 'production') === 'available_soon');
    const hasAvailableSoonActions = availableSoonActions.length > 0;

    const getSelectionForAction = (businessActionKey) => businessActionSelections.find(
        (selection) => selection?.selectedBusinessActionKey === businessActionKey
    );

    const upsertSelection = (nextSelection) => {
        const next = businessActionSelections.filter(
            (selection) => selection?.selectedBusinessActionKey !== nextSelection.selectedBusinessActionKey
        );
        next.push(nextSelection);
        onBusinessActionSelectionsChange?.(next);
    };

    const removeSelection = (businessActionKey) => {
        const next = businessActionSelections.filter(
            (selection) => selection?.selectedBusinessActionKey !== businessActionKey
        );
        onBusinessActionSelectionsChange?.(next);
    };

    const handleActionToggle = (action, enabled) => {
        const businessActionKey = action?.businessActionKey;
        if (!businessActionKey) {
            return;
        }

        if (!enabled) {
            removeSelection(businessActionKey);
            return;
        }

        const eligibleServices = Array.isArray(action?.eligibleServices) ? action.eligibleServices : [];
        if (eligibleServices.length === 1) {
            upsertSelection({
                selectedBusinessActionKey: businessActionKey,
                selectedServiceKey: eligibleServices[0]?.serviceKey || '',
                selectionSource: 'auto'
            });
            return;
        }

        upsertSelection({
            selectedBusinessActionKey: businessActionKey,
            selectedServiceKey: '',
            selectionSource: 'user'
        });
    };

    const handleServiceSelection = (businessActionKey, selectedServiceKey) => {
        upsertSelection({
            selectedBusinessActionKey: businessActionKey,
            selectedServiceKey,
            selectionSource: 'user'
        });
    };

    const renderActionRow = (action) => {
        const selection = getSelectionForAction(action.businessActionKey);
        const capabilityState = action?.capabilityState || 'production';
        const isAvailableSoon = capabilityState === 'available_soon';
        const selectionEnabled = action?.selectionEnabled !== false;
        const isEligible = Boolean(action?.eligible);
        const enabled = Boolean(selection);
        const actionLabel = resolveCustomerFacingLabel(action);
        const eligibleServices = Array.isArray(action.eligibleServices) ? action.eligibleServices : [];
        const singleEligibleService = eligibleServices[0] || null;
        const requiresServiceSelection = enabled && eligibleServices.length > 1;
        const hasServiceSelection = Boolean(selection?.selectedServiceKey);
        const showValidationError = requiresServiceSelection && !hasServiceSelection;

        const hasCompatibleConnectedService = eligibleServices.length > 0;
        const isSingleCompatibleService = eligibleServices.length === 1;
        const isMultiCompatibleService = eligibleServices.length > 1;

        const switchDisabled = !selectionEnabled || !isEligible;
        const serviceSelectorDisabled = isAvailableSoon || !isMultiCompatibleService || !enabled;
        const serviceSelectorValue = isAvailableSoon
            ? ''
            : isSingleCompatibleService
                ? (singleEligibleService?.serviceKey || '')
                : (selection?.selectedServiceKey || '');

        const serviceSelectorPlaceholder = isAvailableSoon
            ? 'Available Soon'
            : hasCompatibleConnectedService
                ? 'Select a connected service...'
                : 'No supported integration connected';
        const serviceSelectorMenuItems = isAvailableSoon
            ? [
                <MenuItem key={`${action.businessActionKey}-available-soon`} value="" disabled>
                    {action?.availabilityTitle || 'Available Soon'}
                </MenuItem>
            ]
            : eligibleServices.map((service) => (
                <MenuItem key={service.serviceKey} value={service.serviceKey}>
                    {service.serviceLabel}
                </MenuItem>
            ));

        return (
            <Box
                key={action.businessActionKey}
                sx={(theme) => ({
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    px: { xs: 1, sm: 1.25 },
                    py: 0.9,
                    backgroundColor: isAvailableSoon ? 'grey.50' : 'background.default'
                })}
            >
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'minmax(0,1fr) auto' },
                        columnGap: 1.25,
                        rowGap: 0.5,
                        alignItems: 'start'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1 }}>
                        <Switch
                            checked={enabled}
                            disabled={switchDisabled}
                            slotProps={{ input: { 'aria-label': actionLabel } }}
                            onChange={(event) => handleActionToggle(action, event.target.checked)}
                        />
                        <Box sx={{ minWidth: 0, textAlign: 'left' }}>
                            <Typography variant="subtitle2" sx={{ lineHeight: 1.3, textAlign: 'left' }}>
                                {actionLabel}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.2, lineHeight: 1.35, textAlign: 'left' }}>
                                {action.shortDescription || ''}
                            </Typography>
                            {isAvailableSoon && (
                                <Typography
                                    variant="caption"
                                    color="primary.main"
                                    sx={{ display: 'block', mt: 0.4, lineHeight: 1.35, textAlign: 'left' }}
                                >
                                    <Box component="span" sx={{ fontWeight: 700 }}>
                                        {action?.availabilityTitle || 'Available Soon'}
                                    </Box>
                                    {' '}
                                    {action?.availabilityMessage || 'Future capability. Will activate as platform support expands.'}
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            width: { xs: '100%', md: 260 },
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: { xs: 'stretch', md: 'flex-end' },
                            rowGap: 0.5
                        }}
                    >
                        <FormControl
                            required={isMultiCompatibleService && enabled}
                            disabled={serviceSelectorDisabled}
                            size="small"
                            sx={{ width: { xs: '100%', md: 260 } }}
                        >
                            <Select
                                value={serviceSelectorValue}
                                displayEmpty
                                inputProps={{ 'aria-label': isAvailableSoon ? 'Available Soon' : 'Connected Service' }}
                                onChange={(event) => handleServiceSelection(action.businessActionKey, event.target.value)}
                                renderValue={(selected) => {
                                    if (isAvailableSoon) {
                                        return action?.availabilityTitle || 'Available Soon';
                                    }
                                    if (!selected) {
                                        return serviceSelectorPlaceholder;
                                    }
                                    const selectedService = eligibleServices.find((service) => service.serviceKey === selected);
                                    return selectedService?.serviceLabel || String(selected);
                                }}
                            >
                                {isAvailableSoon
                                    ? serviceSelectorMenuItems
                                    : [
                                        <MenuItem key={`${action.businessActionKey}-placeholder`} value="" disabled>
                                            {serviceSelectorPlaceholder}
                                        </MenuItem>,
                                        ...serviceSelectorMenuItems
                                    ]}
                            </Select>
                        </FormControl>

                        {showValidationError && (
                            <Typography variant="caption" color="error">
                                Select a connected service.
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Box>
        );
    };

    const renderPhaseFields = () => {
        switch (currentPhase) {
            case 0:
                return (
                    <Stack spacing={2}>
                        <TextField
                            label="Instruction Name"
                            value={instructionName}
                            onChange={(event) => onInstructionNameChange(event.target.value)}
                            required
                            fullWidth
                            autoFocus
                            helperText="Choose a name now so it stays attached to this prompt engineer draft from the start."
                            placeholder="Example: SaaS Support Agent Instructions"
                        />
                    </Stack>
                );
            case 1:
                return (
                    <Stack spacing={2}>
                        {renderSelect('Industry', config.business.industry, industryOptions, (value) => onUpdate('business', 'industry', value))}
                        {renderSelect('Primary Audience', config.business.audience, audienceOptions, (value) => onUpdate('business', 'audience', value))}
                        <Card variant="outlined" sx={{ backgroundColor: 'background.default' }}>
                            <CardContent>
                                <Stack spacing={1.5}>
                                    <Typography variant="subtitle1">Generate Business Description with AI (Optional)</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Provide an About page URL, additional business context, or both. The AI Instruction Architect can use this information to generate a tailored business description for your instructions.
                                    </Typography>
                                    <TextField
                                        label="About Page URL (Optional)"
                                        value={stripUrlProtocol(sourceUrl)}
                                        onChange={(event) => setSourceUrl(buildHttpsUrl(event.target.value))}
                                        placeholder="example.com/about"
                                        error={Boolean(urlError)}
                                        helperText={urlError || 'Provide the URL to your About page, company overview, or service description page and the AI Instruction Architect will use it for summarization.'}
                                        fullWidth
                                        disabled={Boolean(isGeneratingBusinessDescription)}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">https://</InputAdornment>
                                        }}
                                    />
                                    <TextField
                                        label="Products or services, ideal customers, support topics, company tone, differentiators, industries served, or anything else you'd like the AI Instruction Architect to consider."
                                        value={config?.business?.additionalContext || ''}
                                        onChange={(event) => onUpdate('business', 'additionalContext', event.target.value)}
                                        multiline
                                        minRows={3}
                                        placeholder="Add a few sentences about your business, products, differentiators, customers, or goals."
                                        helperText="These notes are prioritized during generation and can be combined with website content if provided."
                                        fullWidth
                                        disabled={Boolean(isGeneratingBusinessDescription)}
                                    />
                                    <Typography variant="body2" color="text.secondary">
                                        Choose one of the options below to generate your business description:
                                    </Typography>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                                        <Tooltip title="Uses your website content, interview responses and optional notes.">
                                            <span>
                                                <Button
                                                    variant="contained"
                                                    onClick={() => requestGenerate('url')}
                                                    disabled={Boolean(isGeneratingBusinessDescription)}
                                                    startIcon={isGeneratingBusinessDescription ? <CircularProgress size={16} color="inherit" /> : null}
                                                >
                                                    Generate From Website
                                                </Button>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="Uses your interview responses and optional notes.">
                                            <span>
                                                <Button
                                                    variant="outlined"
                                                    onClick={() => requestGenerate('context')}
                                                    disabled={Boolean(isGeneratingBusinessDescription)}
                                                >
                                                    Generate From Context
                                                </Button>
                                            </span>
                                        </Tooltip>
                                        <Button
                                            variant="outlined"
                                            onClick={() => setClearConfirmOpen(true)}
                                            disabled={Boolean(isGeneratingBusinessDescription) || !hasExistingDescription}
                                        >
                                            Clear
                                        </Button>
                                    </Stack>
                                    {businessDescriptionGenerationError && !urlError && (
                                        <Typography variant="caption" color="error">
                                            {businessDescriptionGenerationError}
                                        </Typography>
                                    )}
                                </Stack>
                            </CardContent>
                        </Card>
                        <Box>
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                spacing={1}
                                sx={{ mb: 1, flexWrap: 'wrap', rowGap: 0.75 }}
                            >
                                <Typography variant="subtitle2" color="text.primary">
                                    Business Description *
                                </Typography>
                                {isGeneratedDescription && (
                                    <Chip
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        label={getBusinessDescriptionChipLabel()}
                                    />
                                )}
                            </Stack>
                            <TextField
                                value={config.business.description}
                                onChange={handleBusinessDescriptionChange}
                                required
                                fullWidth
                                multiline
                                minRows={5}
                                helperText="Minimum 20 characters"
                                placeholder="Example: We provide managed IT services for small businesses and help customers with support requests, onboarding, billing questions, and general technology guidance."
                                inputProps={{ 'aria-label': 'Business Description' }}
                            />
                        </Box>
                    </Stack>
                );
            case 2:
                return (
                    <Stack spacing={2}>
                        {renderSelect('Primary Personality', config.personality.primary, personalityOptions, (value) => onUpdate('personality', 'primary', value))}
                        {renderSelect('Secondary Personality (Optional)', config.personality.secondary, personalityOptions, (value) => onUpdate('personality', 'secondary', value), false)}
                        {renderSelect('Communication Style', config.personality.communicationStyle, communicationStyleOptions, (value) => onUpdate('personality', 'communicationStyle', value))}
                    </Stack>
                );
            case 3:
                return (
                    <Stack spacing={2}>
                        <MultiSelectField label="Behavior Standards" value={config.behavior.selectedBehaviors} options={behaviorOptions} onChange={(value) => onUpdate('behavior', 'selectedBehaviors', value)} />
                        {renderSelect('When Unsure, The Agent Should...', config.behavior.uncertaintyHandling, uncertaintyHandlingOptions, (value) => onUpdate('behavior', 'uncertaintyHandling', value))}
                        {renderSelect('For Off-Topic Questions', config.behavior.offTopicHandling, offTopicHandlingOptions, (value) => onUpdate('behavior', 'offTopicHandling', value))}
                    </Stack>
                );
            case 4:
                return (
                    <Stack spacing={2}>
                        {renderSelect('Main Job', config.role.mainJob, mainJobOptions, (value) => onUpdate('role', 'mainJob', value))}
                        <MultiSelectField label="Tasks" value={config.role.tasks} options={taskOptions} onChange={(value) => onUpdate('role', 'tasks', value)} />
                        <TextField
                            label="Custom Tasks (Optional)"
                            value={config.role.customTasks}
                            onChange={(event) => onUpdate('role', 'customTasks', event.target.value)}
                            multiline
                            minRows={3}
                        />
                        {renderSelect('Task Approach', config.role.taskApproach, taskApproachOptions, (value) => onUpdate('role', 'taskApproach', value))}
                    </Stack>
                );
            case 5:
                return (
                    <Stack spacing={2}>
                        {businessActionMetadataError && (
                            <Typography variant="caption" color="warning.main">
                                {businessActionMetadataError}
                            </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary">
                            Choose what your AI should be able to accomplish through connected business systems.
                        </Typography>
                        {!hasEligibleConnectedServices && (
                            <Card variant="outlined" sx={{ backgroundColor: 'background.default' }}>
                                <CardContent>
                                    <Stack spacing={0.75}>
                                        <Typography variant="subtitle2">
                                            {actionPhaseEmptyState.title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {actionPhaseEmptyState.message}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {actionPhaseEmptyState.onboardingGuidance}
                                        </Typography>
                                    </Stack>
                                </CardContent>
                            </Card>
                        )}
                        {productionActions.map((action) => renderActionRow(action))}
                        {hasAvailableSoonActions && (
                            <Box sx={{ my: 0.5, borderTop: '1px solid', borderColor: 'divider' }} />
                        )}
                        {hasAvailableSoonActions && (
                            <Typography variant="subtitle2" color="text.secondary" sx={{ pt: 0.5 }}>
                                Available Soon
                            </Typography>
                        )}
                        {availableSoonActions.map((action) => renderActionRow(action))}
                        <Card variant="outlined" sx={{ backgroundColor: 'background.default' }}>
                            <CardContent>
                                <Stack spacing={1}>
                                    <Typography variant="subtitle2">Additional Business Action Settings</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        After assigning these instructions to an agent, you can customize the messages shown
                                        after successful ticket or request submissions, including whether customers see
                                        reference numbers in the creation confirmation.
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        These settings are available under Agent → Business Actions and can be updated
                                        anytime without regenerating your instructions.
                                    </Typography>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Stack>
                );
            case 6:
                return (
                    <Stack spacing={2}>
                        <MultiSelectField label="Success Goals" value={config.success.goals} options={goalOptions} onChange={(value) => onUpdate('success', 'goals', value)} />
                        <MultiSelectField label="Prohibited Actions" value={config.success.prohibitedActions} options={prohibitedActionOptions} onChange={(value) => onUpdate('success', 'prohibitedActions', value)} />
                        <MultiSelectField label="Escalation Triggers" value={config.success.escalationTriggers} options={escalationTriggerOptions} onChange={(value) => onUpdate('success', 'escalationTriggers', value)} />
                    </Stack>
                );
            default:
                return null;
        }
    };

    return (
        <Card elevation={2}>
            <CardContent>
                <Stack spacing={2}>
                    <Box>
                        <Typography variant="overline" color="primary.main">
                            AI Instruction Architect Interview - Phase {displayPhaseNumber} of {displayPhaseCount}
                        </Typography>
                        <Box sx={{ mt: 0.75, display: 'flex', justifyContent: 'center' }}>
                            <LinearProgress
                                variant="determinate"
                                value={progressValue}
                                sx={{
                                    width: { xs: 280, sm: 320 },
                                    maxWidth: '100%',
                                    height: 5,
                                    borderRadius: 999,
                                    backgroundColor: 'rgba(25, 118, 210, 0.12)',
                                    '& .MuiLinearProgress-bar': {
                                        borderRadius: 999,
                                        transition: 'transform 420ms ease'
                                    }
                                }}
                            />
                        </Box>
                        <Typography variant="h6" sx={{ mt: 0.5 }}>
                            {resolvedPhaseTitle}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: '0.94rem', lineHeight: 1.6, color: 'text.secondary' }}>
                            {resolvedPhasePrompt}
                        </Typography>
                    </Box>

                    {renderPhaseFields()}

                    <Stack direction="row" spacing={2}>
                        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={onBack}>
                            Back
                        </Button>
                        <Button variant="contained" onClick={onContinue} disabled={!canContinue}>
                            Continue
                        </Button>
                    </Stack>

                    <Dialog
                        open={replaceConfirmOpen}
                        onClose={() => setReplaceConfirmOpen(false)}
                        maxWidth="sm"
                        fullWidth
                    >
                        <DialogTitle>Replace current business description?</DialogTitle>
                        <DialogContent>
                            <Typography variant="body2" color="text.secondary">
                                Generating with AI will replace your current business description. You can still edit the generated result before continuing.
                            </Typography>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setReplaceConfirmOpen(false)} variant="outlined">
                                Keep Current Text
                            </Button>
                            <Button
                                onClick={() => {
                                    setReplaceConfirmOpen(false);
                                    executeGenerate(pendingGenerationMode, true);
                                }}
                                variant="contained"
                            >
                                Replace with AI Result
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Dialog
                        open={clearConfirmOpen}
                        onClose={() => setClearConfirmOpen(false)}
                        maxWidth="sm"
                        fullWidth
                    >
                        <DialogTitle>Clear business description?</DialogTitle>
                        <DialogContent>
                            <Typography variant="body2" color="text.secondary">
                                This will clear the current business description text so you can start over.
                            </Typography>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setClearConfirmOpen(false)} variant="outlined">
                                Cancel
                            </Button>
                            <Button
                                onClick={() => {
                                    setClearConfirmOpen(false);
                                    onUpdate('business', 'description', '');
                                    onUpdate('business', 'descriptionSource', 'manual');
                                    onUpdate('business', 'descriptionSourceUrl', '');
                                    onUpdate('business', 'descriptionGeneratedAt', '');
                                    onUpdate('business', 'descriptionGenerationMeta', null);
                                    setUrlError('');
                                }}
                                variant="contained"
                                color="warning"
                            >
                                Clear Text
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Stack>
            </CardContent>
        </Card>
    );
}
